import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'

export interface StaffGym {
  gym_id: string
  name: string
  slug: string
  primary_color: string | null
  staff_role: string | null
}

export function useStaffGyms() {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [gyms, setGyms] = useState<StaffGym[]>([])
  const [switching, setSwitching] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/my-staff-gyms`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok && !cancelled) {
          const data = (await res.json()) as { gyms: StaffGym[] }
          setGyms(data.gyms)
        }
      } catch {
        // silently ignore — gym switcher is non-critical
      }
    }
    void load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const switchGym = useCallback(
    async (targetGymId: string, targetSlug: string) => {
      setSwitching(true)
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/switch-active-gym`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ gym_id: targetGymId }),
        })
        if (!res.ok) return
        // Force a fresh token so the new gym_id lands in the JWT before page reload
        await getAccessTokenSilently({ cacheMode: 'off' })
        window.location.href = `/${targetSlug}/dashboard`
      } catch {
        setSwitching(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return { gyms, switching, switchGym }
}
